// Display-only amounts. Never use the inclusive total as a product/stock cost.
const number = value => value === null || value === undefined || value === ''
  ? null : (Number.isFinite(Number(value)) ? Number(value) : null);
const round = (value, digits = 2) => Number(value.toFixed(digits));

export const purchasePriceBreakdown = (price, estimate, quantity = 1) => {
  const qty = number(quantity) ?? 0;
  const amount = number(price?.amount);
  const hasDeposit = Boolean(estimate?.components?.length || Number(estimate?.amount) > 0 || estimate?.complete === false);
  const components = estimate?.components || [];
  const componentCurrenciesKnown = components.length > 0 && components.every(component =>
    component.rate?.currency && number(component.amount) !== null
  );
  const complete = amount !== null && Boolean(price?.currency)
    && (!hasDeposit || (estimate?.complete !== false
      && ((number(estimate?.amount) !== null && Boolean(estimate?.currency)) || componentCurrenciesKnown)));
  const buckets = new Map();
  const add = (currency, field, value) => {
    const key = currency || null;
    if (!buckets.has(key)) buckets.set(key, { currency: key, merchandise: 0, deposit: 0 });
    buckets.get(key)[field] += value;
  };
  add(price?.currency, 'merchandise', (amount ?? 0) * qty);
  if (hasDeposit) {
    if (!estimate?.currency && componentCurrenciesKnown) {
      components.forEach(component => add(component.rate.currency, 'deposit', Number(component.amount) * qty));
    } else {
      add(estimate?.currency, 'deposit', (number(estimate?.amount) ?? 0) * qty);
    }
  }
  return {
    complete, hasDeposit,
    totals: [...buckets.values()].map(bucket => ({ ...bucket,
      total: complete ? bucket.merchandise + bucket.deposit : null,
    })),
  };
};

export const cartItemPriceBreakdown = (item, quantity = item.quantity) => purchasePriceBreakdown(
  item.price, item.depositEstimate ?? item.deposit_estimate ?? item.orderUnit?.deposit_estimate, quantity
);

export const sumPriceBreakdowns = (breakdowns = []) => {
  const buckets = new Map();
  const complete = breakdowns.every(value => value.complete);
  breakdowns.forEach(value => value.totals.forEach(bucket => {
    if (!buckets.has(bucket.currency)) buckets.set(bucket.currency, { currency: bucket.currency, merchandise: 0, deposit: 0 });
    const target = buckets.get(bucket.currency);
    target.merchandise += bucket.merchandise;
    target.deposit += bucket.deposit;
  }));
  return { complete, hasDeposit: breakdowns.some(value => value.hasDeposit),
    totals: [...buckets.values()].map(bucket => ({ ...bucket,
      merchandise: round(bucket.merchandise), deposit: round(bucket.deposit),
      total: complete ? round(bucket.merchandise + bucket.deposit) : null,
    })),
  };
};

export const cartPriceBreakdown = items => sumPriceBreakdowns((items || []).map(item => cartItemPriceBreakdown(item)));

export const storedOrderPriceBreakdown = order => ({
  complete: number(order.total_paid) !== null && Boolean(order.price_currency),
  hasDeposit: Number(order.deposit_total) > 0,
  totals: [{ currency: order.price_currency, merchandise: Number(order.merchandise_total),
    deposit: Number(order.deposit_total), total: number(order.total_paid) }],
});

export const receiptPriceBreakdown = (line, { expected = false, unit = false } = {}) => {
  const quantity = unit ? 1 : Number(expected ? (line.detail?.quantity ?? line.ordered_quantity) : line.quantity) || 0;
  const productAmount = unit
    ? (line.unit_price ?? line.detail?.unit_price)
    : expected
      ? Number(line.detail?.unit_price ?? line.ordered_unit_price ?? line.unit_price) * quantity
      : line.order_price;
  const price = { amount: productAmount, currency: line.price_currency };
  const snapshots = line.deposit_snapshots || [];
  const merchandise = purchasePriceBreakdown(price, null);
  const deposits = snapshots.map(snapshot => {
    const qty = round(Number(snapshot.quantity_per_order_unit || 0) * quantity, 6);
    const rate = number(snapshot.unit_amount);
    const amount = expected && !unit && number(snapshot.expected_amount) !== null
      ? Number(snapshot.expected_amount) : round(qty * (rate ?? 0));
    return { complete: rate !== null && Boolean(snapshot.currency), hasDeposit: true,
      totals: [{ currency: snapshot.currency || null, merchandise: 0, deposit: amount, total: amount }],
    };
  });
  return sumPriceBreakdowns([merchandise, ...deposits]);
};
